
<script>
import tippy from 'tippy.js';
import { Editor, EditorContent, Extension } from 'tiptap';
import { Mention, Placeholder, HardBreak } from 'tiptap-extensions';

const invoiceVars = [
  {
    description: 'Primeiro Nome da Empresa',
    identifier: 'company_first_name'
  },
  {
    description: 'ID da fatura (Bulldesk)',
    identifier: 'invoice_id',
  },
  {
    description: 'URL da fatura (Bulldesk)',
    identifier: 'invoice_url',
  },
  {
    description: 'Valor total da fatura',
    identifier: 'invoice_total',
  },
  {
    description: 'Status da fatura',
    identifier: 'invoice_status',
  },
  {
    description: 'Descrição da fatura',
    identifier: 'invoice_status_description',
  },
  {
    description: 'Código de Barras do Boleto',
    identifier: 'invoice_digitable_line',
  },
];

export default {
  components: {
    EditorContent,
  },

  store: [
    'tenantFields',
  ],

  props: {
    value: {
      type: String,
      required: false,
      default: '',
    },
  },

  mounted () {
    this.editor = new Editor({
      autoFocus: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
      extensions: [
        new HardBreak(),
        /**
         * Custom extension to add <div> on new break lines
         */
        new class extends Extension {
          keys() {
            return {
              Enter(state, dispatch, view) {
                const { schema, doc, tr } = view.state

                const hard_break = schema.nodes.hard_break;

                const transaction = tr.replaceSelectionWith(hard_break.create()).scrollIntoView();

                view.dispatch(transaction);

                return true
              }
            }
          }
        }(),
        new Mention({
          items: () => [...this.tenantFields, ...invoiceVars],

          matcher: {
            char: '{',
            allowSpaces: false,
            startOfLine: false,
          },

          onEnter: ({
            items, query, range, command, virtualNode,
          }) => {
            this.query = query;
            this.filteredFields = items;
            this.suggestionRange = range;
            this.renderPopup(virtualNode);
            this.insertMention = command;
          },

          onChange: ({
            items, query, range, virtualNode,
          }) => {
            this.destroyPopup();

            this.query = query;
            this.filteredFields = items;
            this.suggestionRange = range;
            this.navigatedUserIndex = 0;
            this.renderPopup(virtualNode);
          },

          onExit: () => {
            this.query = null;
            this.filteredFields = [];
            this.suggestionRange = null;
            this.navigatedUserIndex = 0;
            this.destroyPopup();
          },

          onKeyDown: ({ event }) => {
            if (event.keyCode === 13) {
              this.enterHandler();
              return true;
            }

            return false;
          },

          onFilter: (items, query) => {
            if (! query) {
              return items;
            }

            return _.filter(items, (field) => field.description.toLowerCase().indexOf(query.toLowerCase()) !== -1);
          },
        }),
        new Placeholder({
          emptyNodeText: this.__('Escreva a mensagem'),
        }),
      ],

      onUpdate: ({ getHTML }) => {
        /**
         * Replace all <br> to preserve break lines on final text
         */
        const spaces = getHTML().replaceAll(/<br\s*[\/]?>/gi, '\n');

        /**
         * Replace all <span> of mentions on final text
         */
        const value = spaces.replace(/<[^>]*>?/gm, '');

        this.$emit('input', value);
      },
    });

    this.$nextTick(() => {
      if (this.value) {
        /**
         * Replace all \n to preserve break lines on editor
         */
        const value = this.value.replaceAll('\n', '<br>');

        this.editor.setContent(value);
      }
    });
  },

  data () {
    return {
      query: null,
      popup: null,
      editor: null,
      filteredFields: [],
      suggestionRange: null,
      navigatedUserIndex: 0,
      insertMention: () => {},
    };
  },

  watch: {
    activity () {
      this.editor.clearContent();
    },
  },

  computed: {
    hasResults () {
      return this.filteredFields.length;
    },

    showSuggestions () {
      return this.query || this.hasResults;
    },
  },

  methods: {
    mapFields (fields) {
      const mapped = fields.map((field) => ({
        text: field.description,
        value: `{{ ${field.identifier} }}`,
      }));

      return [ ...mapped, ...invoiceVars ];
    },

    enterHandler () {
      const field = this.filteredFields[this.navigatedUserIndex];

      if (field) {
        this.selectField(field);
      }
    },

    selectField ({ identifier }) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: identifier,
          label: `{ ${identifier} }} `,
        },
      });

      this.editor.focus();
    },

    renderPopup (node) {
      if (this.popup) {
        return;
      }

      this.popup = tippy(node, {
        arrow: true,
        inertia: true,
        theme: 'dark',
        showOnInit: true,
        interactive: true,
        performance: true,
        arrowType: 'round',
        duration: [400, 200],
        trigger: 'mouseenter',
        placement: 'top-start',
        content: this.$refs.suggestions,
      });
    },

    destroyPopup () {
      if (this.popup) {
        this.popup.destroyAll();
        this.popup = null;
      }
    },
  },

  beforeDestroy () {
    this.editor.destroy();
  },
};
</script>

<template>
  <div
    class="bdk-relative"
  >
    <div
      class="activity-edit-content"
    >
      <EditorContent
        :editor="editor"
        class="form-control bdk-h-auto"
      />
    </div>

    <div
      ref="suggestions"
      v-show="showSuggestions"
      class="bdk-w-full bdk-max-h-60 bdk-rounded-md bdk-py-1 bdk-text-base bdk-ring-1 bdk-ring-black bdk-ring-opacity-5 bdk-overflow-auto focus:bdk-outline-none sm:bdk-text-sm bdk-cursor-pointer"
    >
      <template
        v-if="hasResults"
      >
        <div
          :key="field.id"
          @click="selectField(field)"
          v-for="(field, index) in filteredFields"
          :class="{ 'is-selected': navigatedUserIndex === index }"
          class="bdk-select-none bdk-relative bdk-py-2 bdk-pl-3 bdk-pr-9 bdk-cursor-pointer hover:bdk-bg-white hover:bdk-bg-opacity-25"
        >
          {{ field.description }}

          <small
            v-text="`- {{ ${field.identifier} }}`"
          />
        </div>
      </template>

      <div
        v-else
        class="is-empty"
      >
        {{ __('Nenhuma variável encontrada') }}.
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .form-control {
    min-height: 52px !important;
  }

  ::v-deep {
    .ProseMirror p.is-editor-empty:first-child::before {
      content: attr(data-empty-text);
      height: 0;
      float: left;
      color: #3B334A;
      font-size: 13px;
      pointer-events: none;
    }

    p,
    p.is-empty {
      margin-bottom: 0px;
    }

    p .mention {
      padding: unset;
      font-size: 13px;
      color: #3B334A;
      font-weight: normal;
      background-color: transparent;
    }
  }
</style>
